import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core'
import { ActivatedRoute } from '@angular/router'

import { Store } from '@ngrx/store'
import { Menu, MenuModule } from 'primeng/menu'

import { FlxIconModule } from '@flowx/angular-ui-toolkit'
import {
  ActionTarget,
  ConversationThreadExecutionPlanActionTargetExpressionType,
  ConversationThreadExecutionPlanActionTargetType,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import { CodeEditorSelection } from '@flowx/designer/modules/monaco-code-editor/types'
import { ApplicationPermissionsStore } from '@flowx/designer/modules/projects/store/application-permissions.store'
import { selectDataModelEntitiesArray } from '@flowx/designer/shared/store/selectors/data-model.selectors'
import { nodesSelector } from '@flowx/designer/shared/store/selectors/node.selectors'
import {
  selectedTemplateConfig,
  selectedTemplateConfigNodeId,
} from '@flowx/designer/shared/store/selectors/template-config.selectors'
import { getTemplateConfigSelectionId } from '@flowx/designer/shared/utils/template-config.utils'

import {
  CodeSelectionContext,
  ExpressionTargetType,
} from '../../../../conversation/ai-conversation.dictionary'
import { AiConversationModel } from '../../../../conversation/ai-conversation.model'
import { DeveloperContextType } from '../../store/developer.actions'

@Component({
  selector: 'flx-ai-code-selection',
  templateUrl: './ai-code-selection.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MenuModule, FlxIconModule],
})
export class AiCodeSelectionComponent {
  #store = inject(Store)
  #activatedRoute = inject(ActivatedRoute)
  #destroyRef = inject(DestroyRef)
  #selectedTemplate = this.#store.selectSignal(selectedTemplateConfig)
  #selectedTemplateNodeId = this.#store.selectSignal(selectedTemplateConfigNodeId)

  #iconStyles = 'flex justify-center items-center w-5 h-5 text-yellow-500'

  selectionMenu = viewChild<Menu>('selectionMenu')

  selection = input<CodeEditorSelection | null>(null)
  contextType = input<DeveloperContextType>(null)
  conversationTargetDescriptor = input<{
    nodeId?: number
    type: ExpressionTargetType
    index?: number
    propName?: string
    actionId?: number
  }>(null)
  menuOptionClicked = output<void>()

  dataModelEntities = this.#store.selectSignal(selectDataModelEntitiesArray)
  nodes = this.#store.selectSignal(nodesSelector)
  // MT-3120: the selection popover is a developer-agent entry point — hide it
  // (in every host editor) when the developer agent is unavailable.
  hasAiDeveloperPermission = inject(ApplicationPermissionsStore).designerAiAccess

  selectionMenuOptions = [
    {
      label: 'Improve Code',
      icon: `${this.#iconStyles} ph ph-sparkle`,
      automationId: 'ai-improve-code-action',
      // TODO: rewire to the AI builder harness
      command: (): void => {},
    },
    {
      label: 'Add Comment',
      icon: `${this.#iconStyles} ph ph-hash`,
      automationId: 'ai-add-comments-action',
      // TODO: rewire to the AI builder harness
      command: (): void => {},
    },
  ]

  #buildActionTarget(): ActionTarget {
    const { templateIds, nodeId } = this.#activatedRoute.snapshot.queryParams
    const descriptor = this.conversationTargetDescriptor()
    const targetNodeId = descriptor?.nodeId ?? nodeId ?? this.#selectedTemplateNodeId()
    const targetElementId = templateIds ?? getTemplateConfigSelectionId(this.#selectedTemplate())

    const isBusinessRule = this.contextType() === DeveloperContextType.BusinessRule

    if (isBusinessRule) {
      return {
        name: '',
        targetType: ConversationThreadExecutionPlanActionTargetType.BusinessRule,
        businessRule: {
          nodeId: targetNodeId?.toString(),
          actionId: descriptor?.actionId?.toString(),
          index: descriptor?.index,
          nodeType: this.nodes().find((node) => node.id === +targetNodeId)?.type,
        },
      }
    }

    return {
      name: '',
      targetType: ConversationThreadExecutionPlanActionTargetType.Expression,
      expression: {
        nodeId: targetNodeId?.toString(),
        elementId: targetElementId?.toString(),
        expressionType:
          descriptor?.type === ExpressionTargetType.Disabled
            ? ConversationThreadExecutionPlanActionTargetExpressionType.Disable
            : ConversationThreadExecutionPlanActionTargetExpressionType.Hide,
      },
    }
  }

  #packageImproveCodeContext(): CodeSelectionContext {
    return {
      code: this.selection()?.code,
      language: this.selection()?.language,
      selection: this.selection(),
      datasource: this.dataModelEntities(),
      aiContentType: AiConversationModel.getAiContentTypeFromDeveloperContextType(
        this.contextType()
      ),
    }
  }

  #packageAddCommentContext(): CodeSelectionContext {
    return {
      code: this.selection()?.code,
      language: this.selection()?.language,
      selection: this.selection(),
      datasource: this.dataModelEntities(),
      aiContentType: AiConversationModel.getAiContentTypeFromDeveloperContextType(
        this.contextType()
      ),
    }
  }
}
